import React, { useState, useEffect } from 'react';
import api from '../utils/api';

interface Resident {
  id: number;
  name: string;
  age: number;
  photo: string | null;
  diagnosis: string;
}

interface AssessmentTemplate {
  id: number;
  name: string;
  type: string;
  description: string;
  scoringCriteria: any;
  maxScore: number;
  interpretationGuide: any;
  frequency: string;
  isActive: boolean;
}

interface AssessmentRecord {
  id: number;
  residentId: number;
  templateId: number;
  assessmentDate: string;
  totalScore: number;
  riskLevel: string | null;
  notes: string | null;
  recommendations: string | null;
  templateName: string;
  templateType: string;
  maxScore: number;
  assessorName: string;
  createdAt: string;
}

const Assessments: React.FC = () => {
  const [residents, setResidents] = useState<Resident[]>([]);
  const [selectedResident, setSelectedResident] = useState<Resident | null>(null);
  const [templates, setTemplates] = useState<AssessmentTemplate[]>([]);
  const [assessments, setAssessments] = useState<AssessmentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AssessmentTemplate | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<AssessmentRecord | null>(null);

  useEffect(() => {
    fetchResidents();
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedResident) {
      fetchAssessments();
    }
  }, [selectedResident]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchResidents = async () => {
    try {
      const response = await api.get('/residents/assigned');
      if (response.data.success) {
        setResidents(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching residents:', error);
      setResidents([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/assessments/templates');
      if (response.data.success) {
        setTemplates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssessments = async () => {
    if (!selectedResident) return;
    
    try {
      const response = await api.get(`/assessments/residents/${selectedResident.id}/assessments`);
      if (response.data.success) {
        setAssessments(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching assessments:', error);
      setAssessments([]);
    }
  };

  const handleRecordAssessment = (template: AssessmentTemplate) => {
    setSelectedTemplate(template);
    setShowRecordModal(true);
  };

  const handleViewAssessment = (assessment: AssessmentRecord) => {
    setSelectedAssessment(assessment);
    setShowHistoryModal(true);
  };

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      nutritional: 'bg-green-100 text-green-800',
      footcare: 'bg-blue-100 text-blue-800',
      cognitive: 'bg-primary-100 text-primary-800',
      pain: 'bg-red-100 text-red-800',
      falls: 'bg-yellow-100 text-yellow-800',
      default: 'bg-gray-100 text-gray-800'
    };
    return colors[type] || colors.default;
  };

  const getRiskLevelColor = (riskLevel: string | null) => {
    if (!riskLevel) return 'bg-gray-100 text-gray-800';
    const lower = riskLevel.toLowerCase();
    if (lower.includes('low')) return 'bg-green-100 text-green-800';
    if (lower.includes('medium') || lower.includes('moderate')) return 'bg-yellow-100 text-yellow-800';
    if (lower.includes('high')) return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading assessments...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Assessments</h1>
      </div>

      <div className="bg-white card-list p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Resident</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {residents.map((resident) => (
            <button
              key={resident.id}
              onClick={() => setSelectedResident(resident)}
              className={`p-4 border-2 rounded-lg text-left transition-all ${
                selectedResident?.id === resident.id
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 hover:border-primary-300'
              }`}
            >
              <div className="flex items-center space-x-3">
                {resident.photo ? (
                  <img
                    src={resident.photo}
                    alt={resident.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                )}
                <div>
                  <h3 className="font-medium text-gray-900">{resident.name}</h3>
                  <p className="text-sm text-gray-500">Age: {resident.age}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {selectedResident && (
        <>
          <div className="bg-white card-list p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Available Assessments for {selectedResident.name}
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <div key={template.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">{template.name}</h3>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${getTypeColor(template.type)}`}>
                        {template.type}
                      </span>
                    </div>
                    <span className="text-sm text-gray-500">Max: {template.maxScore}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{template.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">Frequency: {template.frequency}</span>
                    <button
                      onClick={() => handleRecordAssessment(template)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm"
                    >
                      Record Assessment
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white card-list p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Assessment History</h2>
            {assessments.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No assessments recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assessment
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Risk Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Assessor
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {assessments.map((assessment) => (
                      <tr key={assessment.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(assessment.assessmentDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{assessment.templateName}</div>
                          <div className={`inline-block px-2 py-1 text-xs rounded-full ${getTypeColor(assessment.templateType)}`}>
                            {assessment.templateType}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {assessment.totalScore} / {assessment.maxScore}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {assessment.riskLevel && (
                            <span className={`inline-block px-2 py-1 text-xs rounded-full ${getRiskLevelColor(assessment.riskLevel)}`}>
                              {assessment.riskLevel}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {assessment.assessorName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => handleViewAssessment(assessment)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {showRecordModal && selectedTemplate && selectedResident && (
        <RecordAssessmentModal
          resident={selectedResident}
          template={selectedTemplate}
          onClose={() => {
            setShowRecordModal(false);
            setSelectedTemplate(null);
          }}
          onSuccess={() => {
            fetchAssessments();
            setShowRecordModal(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      {showHistoryModal && selectedAssessment && (
        <ViewAssessmentModal
          assessment={selectedAssessment}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedAssessment(null);
          }}
        />
      )}
    </div>
  );
};

interface RecordAssessmentModalProps {
  resident: Resident;
  template: AssessmentTemplate;
  onClose: () => void;
  onSuccess: () => void;
}

const RecordAssessmentModal: React.FC<RecordAssessmentModalProps> = ({ resident, template, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    assessmentDate: new Date().toISOString().split('T')[0],
    scores: {} as { [key: string]: number },
    notes: '',
    recommendations: '',
    followUpRequired: false,
    followUpDate: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const calculateTotalScore = (scores: { [key: string]: number }) => {
    return Object.values(scores).reduce((sum, score) => sum + score, 0);
  };

  const determineRiskLevel = (totalScore: number) => {
    const levels = template.interpretationGuide?.levels || [];
    for (const level of levels) {
      const range = level.range;
      if (range.includes('+')) {
        const minScore = parseInt(range.replace('+', ''));
        if (totalScore >= minScore) return level.level;
      } else if (range.includes('-')) {
        const [min, max] = range.split('-').map(Number);
        if (totalScore >= min && totalScore <= max) return level.level;
      } else if (totalScore.toString() === range) {
        return level.level;
      }
    }
    return null;
  };

  const handleScoreChange = (sectionId: string, score: number) => {
    setFormData(prev => ({
      ...prev,
      scores: { ...prev.scores, [sectionId]: score }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    // Validate all sections are scored
    const requiredSections = template.scoringCriteria?.sections || [];
    const missingSections = requiredSections.filter((section: any) => 
      formData.scores[section.id] === undefined || formData.scores[section.id] === null
    );

    if (missingSections.length > 0) {
      setError(`Please score all sections. Missing: ${missingSections.map((s: any) => s.name).join(', ')}`);
      setSubmitting(false);
      return;
    }

    const totalScore = calculateTotalScore(formData.scores);
    const riskLevel = determineRiskLevel(totalScore);

    try {
      const response = await api.post(`/assessments/residents/${resident.id}/assessments`, {
        templateId: template.id,
        assessmentDate: formData.assessmentDate,
        scores: formData.scores,
        totalScore,
        riskLevel,
        notes: formData.notes || null,
        recommendations: formData.recommendations || null,
        followUpRequired: formData.followUpRequired,
        followUpDate: formData.followUpDate || null
      });

      if (response.data.success) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to record assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const totalScore = calculateTotalScore(formData.scores);
  const riskLevel = determineRiskLevel(totalScore);

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
            <p className="text-sm text-gray-500">Resident: {resident.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Assessment Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={formData.assessmentDate}
              onChange={(e) => setFormData({ ...formData, assessmentDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Assessment Criteria</h4>
            {template.scoringCriteria?.sections?.map((section: any) => (
              <div key={section.id} className="border border-gray-200 rounded-lg p-4">
                <h5 className="font-medium text-gray-900 mb-2">{section.name}</h5>
                <p className="text-sm text-gray-600 mb-3">{section.description}</p>
                <div className="space-y-2">
                  {section.options.map((option: any) => (
                    <label key={option.value} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        name={section.id}
                        value={option.score}
                        checked={formData.scores[section.id] === option.score}
                        onChange={() => handleScoreChange(section.id, option.score)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                      />
                      <span className="text-sm text-gray-700">
                        {option.label} <span className="text-primary-600 font-medium">(Score: {option.score})</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="font-medium text-gray-900">Total Score:</span>
              <span className="text-2xl font-bold text-primary-600">{totalScore} / {template.maxScore}</span>
            </div>
            {riskLevel && (
              <div>
                <span className="font-medium text-gray-900">Risk Level: </span>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                  riskLevel.toLowerCase().includes('low') ? 'bg-green-100 text-green-800' :
                  riskLevel.toLowerCase().includes('medium') || riskLevel.toLowerCase().includes('moderate') ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {riskLevel}
                </span>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Additional notes or observations..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Recommendations</label>
            <textarea
              value={formData.recommendations}
              onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              placeholder="Care recommendations based on assessment..."
            />
          </div>

          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.followUpRequired}
                onChange={(e) => setFormData({ ...formData, followUpRequired: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Follow-up Required</span>
            </label>
          </div>

          {formData.followUpRequired && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date</label>
              <input
                type="date"
                value={formData.followUpDate}
                onChange={(e) => setFormData({ ...formData, followUpDate: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400"
            >
              {submitting ? 'Recording...' : 'Record Assessment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ViewAssessmentModalProps {
  assessment: AssessmentRecord;
  onClose: () => void;
}

const ViewAssessmentModal: React.FC<ViewAssessmentModalProps> = ({ assessment, onClose }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white mb-10">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{assessment.templateName}</h3>
            <p className="text-sm text-gray-500">
              Assessed on {new Date(assessment.assessmentDate).toLocaleDateString()} by {assessment.assessorName}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-sm text-gray-600">Total Score</span>
                <p className="text-2xl font-bold text-primary-600">{assessment.totalScore} / {assessment.maxScore}</p>
              </div>
              {assessment.riskLevel && (
                <div>
                  <span className="text-sm text-gray-600">Risk Level</span>
                  <p className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${
                    assessment.riskLevel.toLowerCase().includes('low') ? 'bg-green-100 text-green-800' :
                    assessment.riskLevel.toLowerCase().includes('medium') || assessment.riskLevel.toLowerCase().includes('moderate') ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {assessment.riskLevel}
                  </p>
                </div>
              )}
            </div>
          </div>

          {assessment.notes && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Notes</h4>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">{assessment.notes}</p>
            </div>
          )}

          {assessment.recommendations && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
              <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-md">{assessment.recommendations}</p>
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-sm font-medium"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assessments;
